import { useCreationModalProgress } from '@app/src/context/CreationModalProgressContext'
import { Box, Button, DialogContent, Divider, LinearProgress, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import type { PropsWithChildren } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'

const useStyles = makeStyles(({ spacing }) => ({
  progressBar: {
    marginTop: spacing(2),
  },
}))

interface RequestModalContainerProps {
  title?: string
  onClose: () => void
  showProgress?: boolean
  overflow?: 'hidden' | 'visible'
  displayedActiveStep?: number
  displayedTotalSteps?: number
}

const CreationModalContainer: React.FC<PropsWithChildren<RequestModalContainerProps>> = ({
  title,
  onClose,
  showProgress = true,
  children,
  overflow = 'visible',
  displayedActiveStep,
  displayedTotalSteps,
}) => {
  const { formatMessage } = useIntl()
  const { activeStep, totalSteps } = useCreationModalProgress()
  const { progressBar } = useStyles()

  const shownActiveStep = displayedActiveStep ?? activeStep
  const shownTotalSteps = displayedTotalSteps ?? totalSteps
  const progress = shownTotalSteps ? Math.min(Math.round((shownActiveStep / shownTotalSteps) * 100), 100) : 0

  return (
    <DialogContent sx={{ padding: '0px 0px' }}>
      <Box position="sticky" top={0} zIndex={1400} bgcolor="background.paper">
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" px={4} pt={2}>
          <Box display="flex" alignItems="center">
            <Typography variant="subtitle1">{title}</Typography>

            {showProgress && (
              <>
                <Box mx={2}>•</Box>
                <Typography variant="subtitle1" color="textSecondary">
                  {formatMessage(
                    { id: 'form.createRequest.steps' },
                    { activeStep: shownActiveStep, totalSteps: shownTotalSteps },
                  )}
                </Typography>
              </>
            )}
          </Box>

          <Button variant="text" onClick={onClose}>
            {formatMessage({ id: 'general.cancel' })}
          </Button>
        </Box>

        {showProgress ? (
          <LinearProgress className={progressBar} variant="determinate" value={progress} />
        ) : (
          <Divider sx={{ my: 2 }} />
        )}
      </Box>

      <Box display="flex" justifyContent="center" flexGrow={1} height="calc(100vh - 90px)" overflow={overflow}>
        {children}
      </Box>
    </DialogContent>
  )
}

export default CreationModalContainer
